// Intrinsically-sized in-cell content: measured at its rendered size. Anything
// not listed here is fluid — rich text wraps, dividers/charts/forms scale to
// their container — so it imposes no floor and the column stays draggable.
const FIXED_CONTENT_SELECTOR = [
  "img",
  "video",
  "iframe",
  ".buttonWrapper",
  ".iconSizeContainer",
  ".ticker-wrapper",
  ".rkpi",
  '[class*="attachment-"]',
].join(", ");

// td edge → element box: 12px .table-cell-content padding per side.
const IN_CELL_INSET_PX = 24;

/**
 * Per-column px floor derived from in-cell element widths: a column may not
 * be resized below the widest *intrinsically sized* element it currently
 * hosts, so a resize never squashes an image/button or pushes it over
 * neighbouring cells. Sparse array — columns hosting only fluid content stay
 * undefined.
 *
 * Fluid elements deliberately contribute nothing. `.elementContainer` is
 * `tw-w-full`, so measuring it can only ever return the column's own current
 * width — a floor that pins the column exactly where it already is and makes
 * the drag feel stuck. Fluid content reflows instead, and TABLE_COL_MIN_WIDTH
 * remains the absolute floor.
 */
export const measureInCellElementFloors = (
  wrapperEl: HTMLElement,
): number[] => {
  const floors: number[] = [];
  const table = wrapperEl.querySelector("table");
  const containers = wrapperEl.querySelectorAll<HTMLElement>(
    "td[data-cell-id] .table-cell-children .elementContainer",
  );

  containers.forEach((container) => {
    const td = container.closest<HTMLTableCellElement>("td[data-cell-id]");
    // Skip cells of nested tables and merged cells spanning multiple columns.
    if (!td || td.closest("table") !== table || td.colSpan > 1) return;
    const rawCol = td.getAttribute("data-cell-col");
    if (rawCol === null || rawCol.trim() === "") return;
    const col = Number(rawCol);
    if (!Number.isInteger(col) || col < 0) return;

    let width = 0;
    const targets = container.querySelectorAll<HTMLElement>(
      FIXED_CONTENT_SELECTOR,
    );
    targets.forEach((target) => {
      width = Math.max(width, target.getBoundingClientRect().width);
    });
    if (width <= 0) return;

    const floor = Math.ceil(width) + IN_CELL_INSET_PX;
    floors[col] = Math.max(floors[col] ?? 0, floor);
  });

  return floors;
};
